import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { 
  Star, User, LogOut, Settings, Menu, X, Heart, MapPin, 
  Trash2, ChefHat, Calendar as CalendarIcon, BookmarkPlus, Utensils
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Wishlist = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editNotes, setEditNotes] = useState("");
  const [editPriority, setEditPriority] = useState(2);
  
  // Visit dialog state
  const [showVisitDialog, setShowVisitDialog] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [visitDate, setVisitDate] = useState(new Date());
  const [visitNotes, setVisitNotes] = useState("");
  const [isAddingVisit, setIsAddingVisit] = useState(false);

  useEffect(() => {
    const loadWishlist = async () => {
      try {
        const response = await axios.get(`${API}/wishlist`, { withCredentials: true });
        setWishlist(response.data);
      } catch (error) {
        console.warn("Wishlist loading failed:", error);
      } finally {
        setLoading(false);
      }
    };
    loadWishlist();
  }, []);

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`, { withCredentials: true });
      setWishlist(response.data);
    } catch (error) {
      console.warn("Wishlist fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (restaurantId) => {
    try {
      await axios.delete(`${API}/wishlist/${restaurantId}`, { withCredentials: true });
      setWishlist(wishlist.filter(item => item.restaurant_id !== restaurantId));
      toast.success(t('wishlist.removed'));
    } catch (error) {
      toast.error("Failed to remove from wishlist");
    }
  };

  const updateWishlistItem = async (restaurantId) => {
    try {
      await axios.put(
        `${API}/wishlist/${restaurantId}?notes=${encodeURIComponent(editNotes)}&priority=${editPriority}`,
        {},
        { withCredentials: true }
      );
      setWishlist(wishlist.map(item => 
        item.restaurant_id === restaurantId 
          ? { ...item, notes: editNotes, priority: editPriority }
          : item
      ));
      setEditingId(null);
      toast.success("Updated!");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 3: return { label: t('wishlist.high'), color: 'bg-red-500/20 text-red-400' };
      case 2: return { label: t('wishlist.medium'), color: 'bg-yellow-500/20 text-yellow-400' };
      default: return { label: t('wishlist.low'), color: 'bg-green-500/20 text-green-400' };
    }
  };

  const openVisitDialog = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setVisitDate(new Date());
    setVisitNotes("");
    setShowVisitDialog(true);
  };

  const handleAddVisit = async () => {
    if (!selectedRestaurant) return;
    setIsAddingVisit(true);
    try {
      await axios.post(`${API}/visits`, {
        restaurant_id: selectedRestaurant.restaurant_id,
        restaurant_name: selectedRestaurant.name,
        stars: selectedRestaurant.stars,
        visit_date: format(visitDate, 'yyyy-MM-dd'),
        visit_count: 1,
        notes: visitNotes
      }, { withCredentials: true });
      
      // Remove from wishlist after visiting
      await axios.delete(`${API}/wishlist/${selectedRestaurant.restaurant_id}`, { withCredentials: true });
      setWishlist(wishlist.filter(item => item.restaurant_id !== selectedRestaurant.restaurant_id));
      
      toast.success(t('wishlist.visitRecorded', { name: selectedRestaurant.name }));
      setShowVisitDialog(false);
      setSelectedRestaurant(null);
    } catch (error) {
      toast.error(t('wishlist.visitError'));
    } finally {
      setIsAddingVisit(false);
    }
  };

  const navItems = [
    { label: t('common.dashboard'), path: '/dashboard' },
    { label: t('common.restaurants'), path: '/restaurants' },
    { label: t('common.preferences'), path: '/preferences' },
    { label: t('common.community'), path: '/community' },
    { label: t('common.leaderboard'), path: '/leaderboard' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="wishlist-page">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <Star className="w-6 h-6 text-primary fill-primary" />
              <span className="text-xl font-bold gradient-text">Étoile</span>
            </div>

            <nav className="hidden md:flex items-center gap-6">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <span className="hidden sm:inline text-sm">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-card border-border">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    {t('common.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-muted/50" />
                  <DropdownMenuItem onClick={() => logout()} className="cursor-pointer text-red-400">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('common.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight mb-2 flex items-center gap-3">
            <Heart className="w-8 h-8 text-primary fill-primary" />
            {t('wishlist.title')}
          </h1>
          <p className="text-muted-foreground">{t('wishlist.subtitle')}</p>
        </div>

        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {!loading && wishlist.length === 0 && (
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center">
              <Heart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('wishlist.empty')}</h3>
              <p className="text-muted-foreground mb-6">{t('wishlist.emptyDesc')}</p>
              <Button 
                onClick={() => navigate('/restaurants')}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <ChefHat className="w-4 h-4 mr-2" />
                {t('wishlist.browseRestaurants')}
              </Button>
            </CardContent>
          </Card>
        )}
        {!loading && wishlist.length > 0 && (
          <div className="space-y-4">
            {wishlist.map((item) => {
              const restaurant = item.restaurant;
              const priorityInfo = getPriorityLabel(item.priority);
              const isEditing = editingId === item.restaurant_id;
              
              return (
                <Card 
                  key={item.wishlist_id} 
                  className="bg-card border-border hover:border-border transition-colors"
                  data-testid={`wishlist-item-${item.restaurant_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 
                            className="font-semibold text-lg cursor-pointer hover:text-primary transition-colors"
                            onClick={() => navigate('/restaurants')}
                          >
                            {restaurant.name}
                          </h3>
                          <div className="flex gap-0.5">
                            {[...Array(restaurant.stars)].map((_, i) => (
                              <Star key={`wishlist-star-${item.wishlist_id}-${i}`} className="w-4 h-4 text-primary fill-primary" />
                            ))}
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded ${priorityInfo.color}`}>
                            {priorityInfo.label}
                          </span>
                        </div>
                        
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                          <MapPin className="w-3 h-3" />
                          {restaurant.location}, {restaurant.country}
                        </p>
                        
                        <p className="text-xs text-muted-foreground mb-2">
                          {restaurant.cuisine_type}
                        </p>

                        {isEditing ? (
                          <div className="space-y-3 mt-3 p-3 bg-muted/20 rounded">
                            <div>
                              <Label className="text-xs text-muted-foreground">{t('wishlist.priority')}</Label>
                              <Select value={String(editPriority)} onValueChange={(v) => setEditPriority(Number(v))}>
                                <SelectTrigger className="bg-transparent border-border mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-popover border-border">
                                  <SelectItem value="3">{t('wishlist.high')}</SelectItem>
                                  <SelectItem value="2">{t('wishlist.medium')}</SelectItem>
                                  <SelectItem value="1">{t('wishlist.low')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">{t('wishlist.notes')}</Label>
                              <Textarea
                                value={editNotes}
                                onChange={(e) => setEditNotes(e.target.value)}
                                placeholder={t('wishlist.notesPlaceholder')}
                                className="bg-transparent border-border mt-1"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => updateWishlistItem(item.restaurant_id)}>
                                {t('common.save')}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                                {t('common.cancel')}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          item.notes && (
                            <p className="text-sm text-muted-foreground italic mt-2 p-2 bg-muted/10 rounded">
                              &quot;{item.notes}&quot;
                            </p>
                          )
                        )}

                        <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                          <CalendarIcon className="w-3 h-3" />
                          Added {new Date(item.created_at).toLocaleDateString()}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openVisitDialog(restaurant)}
                          className="border-primary/50 hover:border-primary hover:bg-primary/10 text-primary"
                        >
                          <Utensils className="w-4 h-4 mr-1" />
                          {t('wishlist.markAsVisited')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingId(item.restaurant_id);
                            setEditNotes(item.notes || "");
                            setEditPriority(item.priority);
                          }}
                          className="text-muted-foreground"
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFromWishlist(item.restaurant_id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Visit Dialog */}
      <Dialog open={showVisitDialog} onOpenChange={setShowVisitDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Utensils className="w-5 h-5 text-primary" />
              {t('restaurants.recordVisit')}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRestaurant && (
            <div className="space-y-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <h3 className="font-semibold">{selectedRestaurant.name}</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {selectedRestaurant.location}, {selectedRestaurant.country}
                </p>
                <div className="flex gap-0.5 mt-1">
                  {[...Array(selectedRestaurant.stars)].map((_, i) => (
                    <Star key={`dialog-star-${i}`} className="w-4 h-4 text-primary fill-primary" />
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground">{t('restaurants.visitDate')}</Label>
                {/* Native date input for better mobile support */}
                <Input
                  type="date"
                  value={format(visitDate, "yyyy-MM-dd")}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    if (!isNaN(newDate.getTime())) {
                      setVisitDate(newDate);
                    }
                  }}
                  className="w-full mt-1 bg-transparent border-border focus:border-primary text-foreground touch-manipulation"
                  style={{ colorScheme: 'light' }}
                  max={format(new Date(), "yyyy-MM-dd")}
                />
              </div>

              <div>
                <Label className="text-sm text-muted-foreground">{t('restaurants.notes')}</Label>
                <Textarea
                  value={visitNotes}
                  onChange={(e) => setVisitNotes(e.target.value)}
                  placeholder={t('restaurants.notesPlaceholder')}
                  className="bg-transparent border-border mt-1"
                />
              </div>

              <Button
                onClick={handleAddVisit}
                disabled={isAddingVisit}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {isAddingVisit ? t('common.loading') : t('wishlist.saveVisit')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Wishlist;
